import { useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { groupDoctorServiceRows } from "../../../utils/serviceCatalog";
import s from "../clinic.module.css";
import { Button, Input, useToast } from "../../ui";

export default function AdminDoctorServices({ doctorId }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    clinicAPI.getDoctorServices(doctorId).then((r) => {
      const list = r.services || [];
      setRows(list);
      if (selectedId && !list.some((x) => x.id === selectedId)) {
        setSelectedId(null);
      }
    });
  };

  useEffect(() => {
    load();
  }, [doctorId]);

  const selectRow = (id) => {
    setSelectedId((cur) => (cur === id ? null : id));
  };

  const toggleAssigned = (id, e) => {
    e?.stopPropagation?.();
    setRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, assigned: !r.assigned } : r,
      ),
    );
    setSelectedId(id);
  };

  const patch = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await clinicAPI.updateDoctorServices(
        doctorId,
        rows.map((r) => ({
          service_id: r.id,
          assigned: r.assigned,
          duration_minutes: r.duration_minutes
            ? Number(r.duration_minutes)
            : null,
          price: r.price !== "" && r.price != null ? Number(r.price) : null,
        })),
      );
      toast.success("Услуги сохранены");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const assignedCount = rows.filter((r) => r.assigned).length;

  const groups = useMemo(() => groupDoctorServiceRows(rows), [rows]);

  return (
    <div className={s.docTabWrap}>
      <div className={s.docTabIntro}>
        <p>
          Общие услуги и услуги специализации тренера. Если галочки не заданы —
          на портале доступны все подходящие услуги автоматически. Явные галочки
          ограничивают список только выбранными позициями.
        </p>
        <span className={s.docTabBadge}>
          Выбрано: {assignedCount || "все"} / {rows.length}
        </span>
      </div>

      {groups.map((group) => (
        <section key={group.key} className={s.serviceGroup}>
          <h3 className={s.serviceGroupTitle}>{group.title}</h3>
          <div className={s.serviceList}>
            {group.items.map((r) => {
              const isSelected = selectedId === r.id;
              const isOn = !!r.assigned;
              return (
                <div
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  className={`${s.serviceCard} ${isSelected ? s.serviceCardSelected : ""} ${isOn ? s.serviceCardOn : ""}`}
                  onClick={() => selectRow(r.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectRow(r.id);
                    }
                  }}
                >
                  <div className={s.serviceCardMain}>
                    <span
                      className={`${s.serviceCheck} ${isOn ? s.serviceCheckOn : ""}`}
                      onClick={(e) => toggleAssigned(r.id, e)}
                      aria-hidden
                    >
                      {isOn ? "✓" : ""}
                    </span>
                    <div className={s.serviceCardText}>
                      <div className={s.serviceCardName}>{r.name}</div>
                      <div className={s.serviceCardMeta}>
                        по умолчанию {r.default_duration} мин
                        {r.default_price != null
                          ? ` · ${r.default_price} ₽`
                          : ""}
                      </div>
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      className={s.serviceCardEdit}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className={s.serviceField}>
                        <span>Длительность, мин</span>
                        <Input
                          type="number"
                          min={5}
                          disabled={!isOn}
                          value={r.duration_minutes ?? r.default_duration}
                          onChange={(e) =>
                            patch(r.id, "duration_minutes", e.target.value)
                          }
                        />
                      </label>
                      <label className={s.serviceField}>
                        <span>Цена, ₽</span>
                        <Input
                          type="number"
                          disabled={!isOn}
                          value={r.price ?? r.default_price ?? ""}
                          onChange={(e) => patch(r.id, "price", e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div className={s.docTabFooter}>
        <Button type="button" onClick={save} disabled={saving}>
          {saving ? "Сохранение…" : "Сохранить услуги"}
        </Button>
      </div>
    </div>
  );
}
